import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { CheckIcon } from '@heroicons/react/24/outline';
import { Helmet } from 'react-helmet-async';
import api from '../config/axios';
import { System } from '../types';

const Systems = () => {
  const [systems, setSystems] = useState<System[]>([]);
  const [loading, setLoading] = useState(true);
  const [selectedType, setSelectedType] = useState<string>('');
  const [selectedScale, setSelectedScale] = useState<string>('');

  useEffect(() => {
    const fetchSystems = async () => {
      try {
        const response = await api.get('/api/systems');
        setSystems(response.data);
      } catch (error) {
        console.error('Error fetching systems:', error);
      } finally {
        setLoading(false);
      }
    };
    fetchSystems();
  }, []);

  const filteredSystems = systems.filter((system) => {
    const typeMatch = !selectedType || system.type === selectedType;
    const scaleMatch = !selectedScale || system.scale === selectedScale;
    return typeMatch && scaleMatch;
  });

  const testimonials = [
    {
      quote: 'AlonForge turned my chaos into a CRM wave—saved 10h/week!',
      author: 'VA Client',
    },
    {
      quote: 'The inventory system is exactly what my shop needed. Real-time tracking is a game-changer!',
      author: 'Shop Owner',
    },
    {
      quote: 'Professional, affordable, and built for Filipino businesses. Highly recommend!',
      author: 'Freelancer',
    },
  ];

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate">
        <div className="text-xl text-navy">Loading systems...</div>
      </div>
    );
  }

  return (
    <>
      <Helmet>
        <title>AlonForge Solutions | Small-to-Medium IT Systems</title>
        <meta
          name="description"
          content="From solo VA tools to team dashboards—Laravel-powered, React-fronted, affordable for Pinoy businesses"
        />
      </Helmet>

      {/* Hero Section */}
      <section className="bg-gradient-to-br from-navy to-skyblue py-20 relative overflow-hidden">
        <motion.svg
          className="absolute bottom-0 w-full h-32 opacity-20"
          viewBox="0 0 1440 320"
          fill="none"
        >
          <motion.path
            d="M0,96Q96,0 192,96T384,96T576,96T768,96T960,96T1152,96T1344,96L1440,96L1440,320L1344,320L1152,320L960,320L768,320L576,320L384,320L192,320L96,320L0,320Z"
            stroke="#F1F5F9"
            strokeWidth="2"
            fill="none"
            animate={{ pathLength: [0, 1, 0] }}
            transition={{ duration: 4, repeat: Infinity }}
          />
        </motion.svg>

        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            className="text-center text-white"
          >
            <h1 className="text-4xl md:text-6xl font-bold mb-6 font-heading">
              Small-to-Medium IT Systems: Built to Scale Your Flow
            </h1>
            <p className="text-xl md:text-2xl max-w-3xl mx-auto">
              From solo VA tools to team dashboards—Laravel-powered,
              React-fronted, affordable for Pinoy businesses.
            </p>
          </motion.div>
        </div>
      </section>

      {/* Filters */}
      <section className="bg-white py-8 border-b">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex flex-col md:flex-row gap-4 justify-center">
            <select
              value={selectedType}
              onChange={(e) => setSelectedType(e.target.value)}
              className="border border-gray-300 rounded-lg px-4 py-2 bg-white"
            >
              <option value="">All Types</option>
              <option value="CRM">CRM</option>
              <option value="Inventory">Inventory</option>
              <option value="Automation">Automation</option>
            </select>
            <select
              value={selectedScale}
              onChange={(e) => setSelectedScale(e.target.value)}
              className="border border-gray-300 rounded-lg px-4 py-2 bg-white"
            >
              <option value="">All Scales</option>
              <option value="Small">Small</option>
              <option value="Medium">Medium</option>
            </select>
          </div>
        </div>
      </section>

      {/* Case Studies Grid */}
      <section className="py-20 bg-slate">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-3xl font-bold text-center mb-12 text-navy font-heading">
            Our Systems
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {filteredSystems.map((system, index) => (
              <motion.div
                key={system.id}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.6, delay: index * 0.1 }}
                className="bg-white rounded-2xl shadow-md p-8 hover:shadow-xl transition-all duration-300 hover:scale-105"
              >
                {system.image && (
                  <img
                    src={system.image}
                    alt={system.title}
                    className="w-full h-48 object-cover rounded-lg mb-6"
                  />
                )}
                <h3 className="text-2xl font-bold mb-3 text-navy font-heading">
                  {system.title}
                </h3>
                <p className="text-gray-600 mb-4">{system.description}</p>
                <ul className="mb-4 space-y-2">
                  {system.features.map((feature, idx) => (
                    <li key={idx} className="flex items-center">
                      <CheckIcon className="h-5 w-5 text-skyblue mr-2" />
                      <span className="text-sm">{feature}</span>
                    </li>
                  ))}
                </ul>
                <div className="flex items-center justify-between">
                  <span className="bg-navy text-white px-3 py-1 rounded-full text-sm font-semibold">
                    From ₱{system.price.toLocaleString()}
                  </span>
                  <Link
                    to={`/contact?system=${system.id}`}
                    className="bg-skyblue text-white px-6 py-2 rounded-lg hover:bg-blue-600 transition-colors font-semibold"
                  >
                    Request Demo
                  </Link>
                </div>
              </motion.div>
            ))}
          </div>
          {filteredSystems.length === 0 && (
            <div className="text-center py-12">
              <p className="text-gray-600 text-lg">No systems found.</p>
            </div>
          )}
        </div>
      </section>

      {/* Testimonials */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-3xl font-bold text-center mb-12 text-navy font-heading">
            What Clients Say
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {testimonials.map((testimonial, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.6, delay: index * 0.2 }}
                className="bg-slate rounded-xl p-6"
              >
                <p className="text-gray-700 mb-4 italic">"{testimonial.quote}"</p>
                <p className="text-navy font-semibold">— {testimonial.author}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* How We Build Timeline */}
      <section className="py-20 bg-slate">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-3xl font-bold text-center mb-12 text-navy font-heading">
            How We Build
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-4 gap-8">
            {[
              { step: 'Consult', icon: '💬', desc: 'Understand your needs' },
              { step: 'Prototype', icon: '🎨', desc: 'Design & mockup' },
              { step: 'Deploy', icon: '🚀', desc: 'Build & launch' },
              { step: 'Support', icon: '🤝', desc: 'Ongoing maintenance' },
            ].map((item, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, scale: 0.9 }}
                whileInView={{ opacity: 1, scale: 1 }}
                viewport={{ once: true }}
                transition={{ duration: 0.6, delay: index * 0.1 }}
                className="text-center bg-white rounded-xl p-6 shadow-md"
              >
                <div className="text-4xl mb-4">{item.icon}</div>
                <h3 className="text-xl font-bold text-navy mb-2 font-heading">
                  {item.step}
                </h3>
                <p className="text-gray-600">{item.desc}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>
    </>
  );
};

export default Systems;

